import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { User, Users, Package, Menu, X, GraduationCap, Home, Receipt, BookOpen, ChevronLeft, Bell, ClipboardList, LogOut, Crown, FileEdit, Upload, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase, getSupabaseUrl } from '../../supabase/client';

// 定义菜单项类型
interface MenuItem {
  path: string;
  label: string;
  icon: React.ElementType;
  requiresApproval?: boolean; // 是否需要审核通过才能访问
}

// 定义分组类型
interface MenuGroup {
  title: string;
  items: MenuItem[];
}

// 按功能分组的菜单配置
const menuGroups: MenuGroup[] = [
  {
    title: '作业管理',
    items: [
      { path: '/teacher/students', label: '学员管理', icon: Users, requiresApproval: true },
      { path: '/teacher/assignment-publish', label: '发布作业', icon: ClipboardList, requiresApproval: true },
      { path: '/teacher/assignments', label: '阅卷与评讲', icon: BookOpen, requiresApproval: true },
    ],
  },
  {
    title: '试卷管理',
    items: [
      { path: '/teacher/paper-editor', label: '试卷编辑', icon: FileEdit, requiresApproval: true },
      { path: '/teacher/paper-upload', label: '试卷上传', icon: Upload, requiresApproval: true },
      { path: '/teacher/papers', label: '试卷列表', icon: FileText, requiresApproval: true },
      { path: '/teacher/exam-records', label: '答题记录', icon: FileText, requiresApproval: true },
    ],
  },
  {
    title: '个人信息',
    items: [
      { path: '/teacher/basic-info', label: '基本信息', icon: User, requiresApproval: false },
      { path: '/teacher/orders', label: '套餐与订单', icon: Receipt, requiresApproval: true },
      { path: '/teacher/gift-points', label: '我的U币', icon: Crown, requiresApproval: true },
    ],
  },
];

export default function TeacherLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [teacherInfo, setTeacherInfo] = useState<any>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [accessToken, setAccessToken] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    loadTeacherInfo();
    setupRealtimeSubscription();
  }, []);

  const loadTeacherInfo = async () => {
    // getUser() 和 getSession() 可能因 Supabase auth lock 竞争导致 AbortError
    let user: any = null;
    try {
      const { data } = await supabase.auth.getUser();
      user = data.user;
    } catch (lockError: any) {
      if (lockError?.name === 'AbortError' || lockError?.message?.includes('Lock broken')) {
        const { data } = await supabase.auth.getUser();
        user = data.user;
      } else {
        throw lockError;
      }
    }
    if (!user) {
      navigate('/login');
      return;
    }

    let session: any = null;
    try {
      const { data } = await supabase.auth.getSession();
      session = data.session;
    } catch (lockError: any) {
      if (lockError?.name === 'AbortError' || lockError?.message?.includes('Lock broken')) {
        const { data } = await supabase.auth.getSession();
        session = data.session;
      } else {
        throw lockError;
      }
    }
    const token = session?.access_token || '';
    setAccessToken(token);

    const { data: profileData } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (!profileData || profileData.role !== 'teacher') {
      navigate('/');
      return;
    }

    setTeacherInfo(profileData);
    loadUnreadCount(user.id, token);
  };

  // 设置 Realtime 订阅监听审核状态变化
  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel('teacher-audit-status-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
        },
        (payload) => {
          // 检查是否是当前用户的更新
          if (payload.new.id === teacherInfo?.id) {
            const newStatus = payload.new.audit_status;
            if (newStatus && newStatus !== teacherInfo.audit_status) {
              // 状态变化，刷新数据并提示
              loadTeacherInfo();
              if (newStatus === 'approved') {
                alert('恭喜！您的教师认证已通过审核，现在可以使用全部功能了');
              } else if (newStatus === 'rejected') {
                alert('审核未通过，请查看站内信了解详情');
              }
            }
          }
        }
      )
      .subscribe();

    // 组件卸载时清理订阅
    return () => {
      supabase.removeChannel(channel);
    };
  };

  const loadUnreadCount = async (userId: string, token: string) => {
    try {
      if (!token) return;
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'messages_get_unread_count', userId }),
      });
      const result = await response.json();
      if (result.success) {
        setUnreadCount(result.count);
      }
    } catch (error) {
      console.error('加载未读消息数失败:', error);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // 处理菜单点击事件
  const handleMenuClick = (e: React.MouseEvent, item: MenuItem) => {
    const isApproved = teacherInfo?.audit_status === 'approved';
    
    if (item.requiresApproval && !isApproved) {
      e.preventDefault();
      alert('审核通过后方可访问');
      return;
    }
  };

  // 判断是否为允许访问的路径（未审核状态下）
  const isAllowedPath = (path: string) => {
    const isApproved = teacherInfo?.audit_status === 'approved';
    if (isApproved) return true;
    
    // 未审核状态下只允许访问基本信息和站内信
    const allowedPaths = ['/teacher/basic-info', '/teacher/messages'];
    return allowedPaths.includes(path);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className={`bg-white border-r border-gray-200 transition-all duration-300 flex flex-col ${sidebarOpen ? 'w-64' : 'w-16'}`}>
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-gray-800">教师中心</span>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-600">
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        <nav className="p-3 space-y-4 flex-1 overflow-y-auto">
          {menuGroups.map((group, groupIndex) => (
            <div key={groupIndex}>
              {/* 分组标题 */}
              {sidebarOpen && (
                <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {group.title}
                </div>
              )}

              {/* 分组菜单项 */}
              <div className="space-y-1">
                {group.items.map((item) => {
                  const isApproved = teacherInfo?.audit_status === 'approved';
                  const isDisabled = item.requiresApproval && !isApproved;
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={(e) => handleMenuClick(e, item)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                        isActive(item.path)
                          ? 'bg-purple-500 text-white shadow-md'
                          : isDisabled
                          ? 'text-gray-400 cursor-not-allowed opacity-50'
                          : 'text-gray-600 hover:bg-purple-50 hover:text-purple-600'
                      }`}
                    >
                      <item.icon 
                        size={18} 
                        className={
                          isActive(item.path) 
                            ? 'text-white' 
                            : isDisabled 
                            ? 'text-gray-400' 
                            : 'text-gray-500'
                        } 
                      />
                      {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
                    </Link>
                  );
                })}
              </div>

              {/* 分组之间的分隔线（最后一个分组不显示） */}
              {groupIndex < menuGroups.length - 1 && sidebarOpen && (
                <div className="my-3 border-t border-gray-100"></div>
              )}
            </div>
          ))}
        </nav>

        {/* 退出登录按钮 */}
        <div className="p-3 border-t border-gray-200 mt-auto">
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all w-full ${
              sidebarOpen
                ? 'text-red-600 hover:bg-red-50'
                : 'text-red-600 hover:bg-red-50 justify-center'
            }`}
          >
            <LogOut size={18} className="text-red-500" />
            {sidebarOpen && <span className="text-sm font-medium">退出登录</span>}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shadow-sm">
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors shadow-md"
            >
              <Home size={18} />
              <span className="text-sm font-medium">首页</span>
            </Link>
            <Link
              to="/exam-categories"
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors shadow-md"
            >
              <BookOpen size={18} />
              <span className="text-sm font-medium">刷题平台</span>
            </Link>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-md"
            >
              <ChevronLeft size={18} />
              <span className="text-sm font-medium">返回</span>
            </button>
            {/* 站内信 - 放在返回按钮右边 */}
            <Link
              to="/teacher/messages"
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-500 text-white rounded-xl shadow-lg shadow-amber-200/50 hover:shadow-amber-300/60 hover:scale-105 transition-all duration-300 group"
            >
              {/* 信封图标容器 */}
              <div className="relative">
                <svg
                  className="w-6 h-6 text-white drop-shadow-sm"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {/* 信封主体 */}
                  <rect x="2" y="5" width="20" height="14" rx="2" className="fill-amber-100/20" />
                  {/* 信封翻盖 - V形 */}
                  <path d="M2 7l10 7 10-7" className="stroke-white" />
                  {/* 信封底部装饰线 */}
                  <path d="M2 17l6-4M22 17l-6-4" className="stroke-white/60" />
                </svg>
                {/* 未读数红点 */}
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-md border-2 border-amber-400 animate-pulse">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
              <span className="text-sm font-bold text-white drop-shadow-sm">站内信</span>
            </Link>
          </div>
          {/* 欢迎信息 - 居中 */}
          <div className="flex-1 flex items-center justify-center">
            <span className="text-sm text-gray-600">欢迎你，{teacherInfo?.teacher_nickname || teacherInfo?.username || '老师'}</span>
          </div>
          {/* 占位元素保持布局平衡 */}
          <div className="w-[200px]"></div>
        </header>
        <main className="flex-1 p-6 overflow-auto bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
